/**
 * Adaptive expenditure: how many calories you actually burn, learned from what
 * you ate and what the scale did — the idea at the heart of MacroFactor.
 *
 * Energy balance over a window: intake − expenditure = stored energy change,
 * and ~7700 kcal is a kilogram of body mass. So an observation is
 *
 *     obs = mean valid intake − 7700 × (weight slope, kg/day)
 *
 * over the last 21 days. One window is noisy (a kilo of water reads as 7700 kcal),
 * so the estimate moves toward each observation slowly and in proportion to
 * how much data backs it:
 *
 *     c   = min(1, n/14) · min(1, m/10) · min(1, span/14)
 *     E_d = E_{d-1} + clamp(0.25 · c · (obs − E_{d-1}), ±200/7)
 *
 * n = valid intake days in the window, m = weigh-in days, span = first-to-last
 * weigh-in. The step cap limits the estimate to ~200 kcal a week of movement,
 * and it is bounded to 0.9–2.6 × BMR. Until there's enough data (7 logged days,
 * 5 weigh-ins spanning 10 days) there is no observation and the estimate holds
 * at the prior, Mifflin-St Jeor × activity.
 *
 * Deterministic and recomputed from the full history: no stored state to drift.
 */

import { addDays, daysBetween, dayRange } from './days.ts'
import { olsSlope, type TrendPoint } from './trend.ts'

export const KCAL_PER_KG = 7700
export const WINDOW = 21
const MAX_STEP_PER_DAY = 200 / 7

export type IntakeDay = { kcal: number; valid: boolean }
export type ExpenditurePoint = {
  day: string; E: number; obs: number | null; c: number; n: number; m: number
}

export function expenditureSeries(opts: {
  from: string
  to: string
  prior: number
  bmr: number
  intake: Map<string, IntakeDay>
  weights: TrendPoint[]
}): ExpenditurePoint[] {
  const { from, to, prior, bmr, intake, weights } = opts
  if (from > to) return []
  const lo = 0.9 * bmr, hi = 2.6 * bmr
  let E = Math.max(lo, Math.min(hi, prior))
  const out: ExpenditurePoint[] = []
  const wByDay = new Map(weights.map((w) => [w.day, w.winsorised]))

  for (const d of dayRange(from, to)) {
    const start = addDays(d, -(WINDOW - 1))
    let n = 0, sum = 0
    const xs: number[] = [], ys: number[] = []
    for (const day of dayRange(start, d)) {
      const it = intake.get(day)
      if (it?.valid) { n++; sum += it.kcal }
      const w = wByDay.get(day)
      if (w !== undefined) { xs.push(daysBetween(start, day)); ys.push(w) }
    }
    const m = xs.length
    const span = m ? xs[m - 1] - xs[0] : 0
    let obs: number | null = null
    if (n >= 7 && m >= 5 && span >= 10) {
      const slope = olsSlope(xs, ys)
      if (slope !== null) {
        obs = Math.max(0.8 * bmr, Math.min(3 * bmr, sum / n - KCAL_PER_KG * slope))
      }
    }
    const c = Math.min(1, n / 14) * Math.min(1, m / 10) * Math.min(1, span / 14)
    if (obs !== null) {
      const step = Math.max(-MAX_STEP_PER_DAY, Math.min(MAX_STEP_PER_DAY, 0.25 * c * (obs - E)))
      E = Math.max(lo, Math.min(hi, E + step))
    }
    out.push({ day: d, E, obs, c, n, m })
  }
  return out
}

/** Is a day's log trustworthy enough to learn from? */
export function isValidDay(kcal: number, entries: number,
  status: 'auto' | 'complete' | 'incomplete' | undefined, targetKcal: number): boolean {
  if (status === 'incomplete') return false
  if (status === 'complete') return true
  if (entries === 0) return false
  // Unmarked and under 40% of target: almost certainly half-logged.
  return kcal >= 0.4 * targetKcal
}

export function confidenceLabel(c: number): 'low' | 'building' | 'solid' {
  return c >= 0.75 ? 'solid' : c >= 0.3 ? 'building' : 'low'
}
